import { useState } from 'react';
import axios from 'axios';
import Dropzone from 'react-dropzone';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import get from 'lodash/get';

import { FormError, HelpText } from '@Forms/fields.jsx';
import { fileAsBase64, getCookieValue } from '@helpers';
import { PhotosIcon } from '@svgs';

const ACCEPTED_IMAGE_TYPES = {
  'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.mpo', '.webp'],
};

const ImageField = ({
  altTagName,
  className,
  helpText,
  label,
  name,
  setIsUploading,
  options = {},
  serializationType = 'nested',
  useFormReturn,
  ...props
}) => {
  // resuable image field

  // unpacked form vars from useForm
  const {
    clearErrors,
    register,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useFormReturn;

  // local state for upload progress
  const [progress, setProgress] = useState(-1);

  // state for if the edit alt tag field is open
  const [editingAlt, setEditingAlt] = useState(false);

  // default nested serialization - eg.
  // "field_name": {
  //   "id": 12345,
  //   "absolute_path": "/img/upload/some-image.jpg"
  // }
  let idField = `${name}.id`;
  let imagePathField = `${name}.absolute_path`;

  if (serializationType == 'flat') {
    // flat serialization - eg.
    // "field_name_id": 12345
    // "field_name": "/img/upload/some-image.jpg"
    idField = `${name}_id`;
    imagePathField = `${name}`;
  }

  // which field should indicate errors
  let error = get(errors, idField);

  const clearImage = () => {
    // clear the image
    setValue(idField, null);
    setValue(imagePathField, null);
  };

  const onDrop = (files, fileRejections) => {
    // clear any errors that were previously there
    clearErrors(idField);
    console.log('onDrop', files, fileRejections);
    if (fileRejections.length || !files.length) {
      setProgress(-1);
      let errorCode = fileRejections[0]?.errors[0]?.code;
      let errorMessage = '';
      switch (errorCode) {
        case 'file-invalid-type':
          errorMessage = 'Please upload a supported image file type.';
          break;
        case 'file-too-large':
          errorMessage = 'Please upload a smaller image file.';
          break;
        default:
          errorMessage = 'Please upload a valid image file.';
          break;
      }

      setError(idField, {
        type: 'custom',
        message: errorMessage,
      });
      if (setIsUploading) {
        setIsUploading(false);
      }
      return;
    }

    if (setIsUploading) {
      // if state func passes, set state to false
      setIsUploading(true);
    }
    // Adding a new image
    // get the image base64 data
    fileAsBase64(files, (data) => {
      // upload the image
      const payload = {
        type: 'image',
        upload: data,
      };
      axios
        .post('/api/v2/images/', payload, {
          onUploadProgress: (ev) => {
            setProgress(Math.round((ev.loaded * 100) / ev.total));
          },
          headers: {
            'content-type': 'application/json',
            'x-csrftoken': getCookieValue('csrftoken'),
          },
        })
        .then((response) => {
          console.log('image response', response);
          // clear any errors that were previously there
          clearErrors(idField);
          setValue(imagePathField, response.data.absolute_path);
          setValue(idField, response.data.id);
          setProgress(-1);
          if (setIsUploading) {
            // if state func passes, set state to false
            setIsUploading(false);
          }
          return response.data;
        })
        .catch((error) => {
          console.error('image error', error);
          setProgress(-1);
          const errorMessage =
            error.response?.status === 413
              ? 'Image too large. Max image upload size is 3MB'
              : 'There was an error uploading the image.';
          setError(idField, {
            type: 'custom',
            message: errorMessage,
          });
          //setProgress('There was an error loading the image.');
          if (setIsUploading) {
            // if state func passes, set state to false
            setIsUploading(false);
          }
        });
    });
  };

  return (
    <div
      className={
        `sb-form-item-formgroup sb-form-item-upload ` +
        (error ? 'sb-form-item-error' : '')
      }
    >
      <label htmlFor={name}>{label}</label>
      <div className="sb-form-item-upload-wrap">
        <input type="hidden" {...register(idField, options)} />
        <input type="hidden" {...register(imagePathField)} />
        {(!progress || progress < 0) && watch(idField) && (
          <div className="sb-form-item-uploaded-image">
            <img src={watch(imagePathField)} alt="" />
            {altTagName && !editingAlt && (
              <input type="hidden" {...register(altTagName)} />
            )}
            {altTagName && !editingAlt && watch(altTagName) && (
              <div className="sb-uploaded-image-alt-title">
                <p>
                  <b>Alt tag:</b> <i>{watch(altTagName)}</i>
                </p>
              </div>
            )}
            {altTagName && editingAlt && (
              <div className="sb-flex sb-uploaded-image-alt-wrap">
                <p className="st-label">Image alt tag</p>
                <input
                  type="text"
                  placeholder="Enter alt text"
                  {...register(altTagName)}
                ></input>
                <button
                  className="sb-btn sb-btn-cancel"
                  type="button"
                  onClick={() => setEditingAlt(!editingAlt)}
                >
                  Done
                </button>
              </div>
            )}
            <div className="sb-flex">
              {altTagName && !editingAlt && (
                <button
                  type="button"
                  onClick={() => setEditingAlt(!editingAlt)}
                >
                  Edit alt tag
                </button>
              )}
              <Dropzone
                accept={ACCEPTED_IMAGE_TYPES}
                maxFiles={1}
                onDrop={onDrop}
              >
                {({ getRootProps, getInputProps }) => (
                  <>
                    <button type="button" {...getRootProps()}>
                      Click to change image
                    </button>
                    <input id={name} {...getInputProps()} />
                  </>
                )}
              </Dropzone>
              {!options.required && watch(idField) && (
                <button type="button" onClick={clearImage}>
                  Clear image
                </button>
              )}
            </div>
          </div>
        )}
        {((typeof progress === 'string' && progress.length) ||
          (!watch(idField) && (!progress || progress < 0))) && (
          <Dropzone accept={ACCEPTED_IMAGE_TYPES} maxFiles={1} onDrop={onDrop}>
            {({ getRootProps, getInputProps }) => (
              <div className="sb-clickable" {...getRootProps()}>
                <PhotosIcon />
                <p className="sb-h2">Drag &amp; drop an image here</p>
                <span className="sb-image-upload-submit">
                  Click to select and upload
                </span>
                <input id={name} {...getInputProps()} />
              </div>
            )}
          </Dropzone>
        )}
        {progress > 0 && (
          <>
            <PhotosIcon />
            <div className="sb-form-item-upload-progress-wrap">
              <div className="sb-form-item-upload-progress">
                <div
                  className="sb-form-item-upload-progress-bar"
                  style={{ width: `${progress - 12}%` }}
                ></div>
              </div>
            </div>
            <p>Your image is uploading...</p>
          </>
        )}
        {typeof progress !== 'string' && error && progress <= 0 && (
          <FormError message={error.message} />
        )}
        {helpText && <HelpText message={helpText} />}
      </div>
    </div>
  );
};

export default ImageField;
